import { baseApi } from "@/api/baseApi";
import { SPEND_TAGS } from "@/api/spend.constraints";
import { SUPPLIER_TAGS } from "@/api/suppliers.constraints";
import type {
  RateConfiguration,
  RateDiagnostics,
  RateInventory,
  RatePreview,
  RateReceipt,
} from "@/types/exchange-rates";
import type { SpendListParams } from "@/types/spend";

const BASE = "/pf-mainweb/suppliers/spend/exchange-rates";
export const exchangeRatesApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getExchangeRates: builder.query<
      RateInventory,
      {
        source?: string;
        currency?: string;
        history: boolean;
        offset: number;
        limit: number;
        history_offset?: number;
        history_limit?: number;
      }
    >({
      query: params => ({ url: BASE, method: "GET", params }),
      providesTags: [SPEND_TAGS.FAMILIES],
    }),
    getRateDiagnostics: builder.query<RateDiagnostics, SpendListParams>({
      query: params => ({ url: `${BASE}/diagnostics`, method: "GET", params }),
      providesTags: [SPEND_TAGS.SERIES],
    }),
    previewExchangeRates: builder.mutation<RatePreview, { file: File; configuration: RateConfiguration }>({
      query: ({ file, configuration }) => {
        const data = new FormData();
        data.append("file", file);
        data.append("configuration", JSON.stringify(configuration));
        return { url: `${BASE}/preview`, method: "POST", data };
      },
    }),
    previewMissingRatePeriod: builder.mutation<
      RatePreview,
      RateConfiguration & { reference_rate_id: string; approved: true }
    >({
      query: data => ({ url: `${BASE}/missing-period`, method: "POST", data }),
    }),
    getExchangeRatePreview: builder.query<RatePreview, string>({
      query: id => ({ url: `${BASE}/previews/${encodeURIComponent(id)}`, method: "GET" }),
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.result)
            dispatch(
              baseApi.util.invalidateTags([
                ...Object.values(SPEND_TAGS),
                SUPPLIER_TAGS.SUPPLIER_VALUES,
                SUPPLIER_TAGS.SUPPLIER_DETAIL,
                SUPPLIER_TAGS.SUPPLIER_FAMILIES,
              ]),
            );
        } catch {
          /* Failed retrieval leaves the reviewed preview available for retry. */
        }
      },
    }),
    applyExchangeRates: builder.mutation<RateReceipt, string>({
      query: id => ({ url: `${BASE}/previews/${encodeURIComponent(id)}/apply`, method: "POST" }),
      invalidatesTags: (_result, error) =>
        error
          ? []
          : [
              ...Object.values(SPEND_TAGS),
              SUPPLIER_TAGS.SUPPLIER_VALUES,
              SUPPLIER_TAGS.SUPPLIER_DETAIL,
              SUPPLIER_TAGS.SUPPLIER_FAMILIES,
            ],
    }),
  }),
});
export const {
  useGetExchangeRatesQuery,
  useGetRateDiagnosticsQuery,
  usePreviewExchangeRatesMutation,
  usePreviewMissingRatePeriodMutation,
  useApplyExchangeRatesMutation,
  useLazyGetExchangeRatePreviewQuery,
} = exchangeRatesApi;
